import { useEffect, useMemo, useRef, useState } from 'react'
import type { Options } from 'highcharts'
import { buildHighstockOptions } from './buildHighstockOptions'
import type { LabRateTick } from './fetchRunnerRates'
import { fetchRunnerRates } from './fetchRunnerRates'
import { loadHighchartsStock } from './loadHighchartsStock'
import type { SurveillanceMarketMeta, SurveillanceRunner } from './types'

/** Embedded static demo rates are `[sec, back]` only — promote to Lab ticks. */
function embeddedRatesToTicks(
  rates: [number, number][] | undefined,
): LabRateTick[] {
  if (!rates?.length) return []
  return rates.map(([d, b]) => ({ d, b }))
}

type StockChart = {
  destroy: () => void
}

export function RunnerOddsChart({
  runner,
  market,
}: {
  runner: SurveillanceRunner
  market: SurveillanceMarketMeta
}) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const chartRef = useRef<StockChart | null>(null)
  const hasEmbedded = Boolean(runner.rates?.length)
  const [ticks, setTicks] = useState<LabRateTick[] | null>(
    hasEmbedded ? embeddedRatesToTicks(runner.rates) : null,
  )
  const [rateError, setRateError] = useState<string | null>(null)
  const [rateLoading, setRateLoading] = useState(!hasEmbedded)

  useEffect(() => {
    if (hasEmbedded) {
      setTicks(embeddedRatesToTicks(runner.rates))
      setRateError(null)
      setRateLoading(false)
      return
    }
    let cancelled = false
    setRateLoading(true)
    setRateError(null)
    void fetchRunnerRates(runner.u, runner.fu).then((res) => {
      if (cancelled) return
      if (res.ok) {
        setTicks(res.ticks)
        setRateError(null)
      } else {
        setTicks(null)
        setRateError(res.error)
      }
      setRateLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [hasEmbedded, runner.rates, runner.u, runner.fu])

  const options = useMemo((): Options | null => {
    if (!ticks?.length && !runner.b.length) return null
    return buildHighstockOptions({
      market,
      runnerName: runner.n,
      ticks: ticks ?? [],
      bets: runner.b,
    })
  }, [ticks, runner.b, runner.n, market])

  useEffect(() => {
    const el = hostRef.current
    if (!el || !options || rateLoading || rateError) {
      chartRef.current?.destroy()
      chartRef.current = null
      return
    }

    let cancelled = false
    void loadHighchartsStock().then((Highcharts) => {
      if (cancelled || !hostRef.current) return
      chartRef.current?.destroy()
      chartRef.current = Highcharts.stockChart(hostRef.current, options)
    })

    return () => {
      cancelled = true
      chartRef.current?.destroy()
      chartRef.current = null
    }
  }, [options, rateLoading, rateError])

  return (
    <figure className="cm-surv-chart cm-surv-chart-highstock">
      {rateLoading ? (
        <p className="cm-surv-rate-status">Loading Data for Graph</p>
      ) : null}
      {rateError ? (
        <pre className="cm-surv-rate-error" role="alert">
          Error occurred: {rateError}
        </pre>
      ) : null}
      {!rateLoading && !rateError ? (
        <div
          ref={hostRef}
          className="cm-surv-highstock-host"
          role="img"
          aria-label={`Odds timeline for ${runner.n}`}
        />
      ) : null}
      <div className="cm-surv-chart-meta">
        <span>
          {runner.b.length} bet{runner.b.length === 1 ? '' : 's'}
        </span>
      </div>
    </figure>
  )
}
